import { useState } from "react";
import { CheckCircle2, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";

const formatPhone = (raw: string) => {
  const d = raw.replace(/\D/g, "").replace(/^1/, "").slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

const ASK_HR_NOTE = `Hi — I'd love for us to offer Home Approach as an employee benefit. It's a homeownership and financial wellness program: a readiness score, down payment assistance matched to where we live, and a personal guide from today through closing (plus tools for current homeowners). Employers pay for it, never employees, and Home Approach takes no commission from agents or lenders. Info: https://www.homeapproach.com/lp/organizations — or partnerships@homeapproach.com.`;

interface Props {
  source?: string;
  /** Prefill for signed-in members. */
  defaults?: { firstName?: string; lastName?: string; email?: string };
  compact?: boolean;
  onDone?: () => void;
}

export default function EmployerAccessForm({ source = "request-access", defaults, compact, onDone }: Props) {
  const [f, setF] = useState({
    firstName: defaults?.firstName ?? "",
    lastName: defaults?.lastName ?? "",
    email: defaults?.email ?? "",
    phone: "",
    employerName: "",
    hrContactName: "",
    hrContactEmail: "",
    mayNameMember: false,
    website: "",
  });
  const [state, setState] = useState<"idle" | "sending" | "done">("idle");
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const set = (k: keyof typeof f, v: string | boolean) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!f.firstName.trim() || !f.lastName.trim() || !f.email.trim() || f.employerName.trim().length < 2) {
      setError("Please add your name, email and employer.");
      return;
    }
    if (f.phone && f.phone.replace(/\D/g, "").length !== 10) {
      setError("Please enter a 10-digit phone number or leave it blank.");
      return;
    }
    setState("sending");
    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      const res = await fetch("/api/public/employer-access-request", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ ...f, source, sourcePath: window.location.pathname + window.location.search }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || "We couldn't save your request. Please email partnerships@homeapproach.com.");
      trackEvent("employer_access_requested", { source, may_name: f.mayNameMember });
      setState("done");
      onDone?.();
    } catch (err) {
      setError((err as Error).message);
      setState("idle");
    }
  };

  if (state === "done") {
    return (
      <div className="rounded-2xl border border-border bg-card p-6 text-center">
        <CheckCircle2 className="mx-auto h-10 w-10 text-primary" aria-hidden />
        <h2 className="mt-3 text-xl font-semibold text-foreground">You're on the list</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          We'll reach out to {f.employerName.trim() || "your employer"} and let you know when Home Approach is live for you.
        </p>
        <div className="mt-5 rounded-xl border border-border bg-muted/50 p-4 text-left">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Want to speed it up? Send this to HR</p>
          <p className="mt-2 text-sm text-foreground whitespace-pre-wrap">{ASK_HR_NOTE}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => {
              navigator.clipboard?.writeText(ASK_HR_NOTE).then(() => setCopied(true), () => {});
              trackEvent("employer_access_hr_note_copied", { source });
            }}
          >
            <Copy className="h-4 w-4" aria-hidden /> {copied ? "Copied" : "Copy note"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={`space-y-4 rounded-2xl border border-border bg-card ${compact ? "p-4" : "p-6"} text-left`} noValidate>
      <input type="text" name="website" value={f.website} onChange={(e) => set("website", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden />
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label htmlFor="ea-first">First name</Label>
          <Input id="ea-first" value={f.firstName} onChange={(e) => set("firstName", e.target.value)} autoComplete="given-name" required />
        </div>
        <div className="space-y-1">
          <Label htmlFor="ea-last">Last name</Label>
          <Input id="ea-last" value={f.lastName} onChange={(e) => set("lastName", e.target.value)} autoComplete="family-name" required />
        </div>
      </div>
      <div className="space-y-1">
        <Label htmlFor="ea-email">Personal email</Label>
        <Input id="ea-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} autoComplete="email" required />
      </div>
      <div className="space-y-1">
        <Label htmlFor="ea-phone">Mobile (optional)</Label>
        <Input id="ea-phone" type="tel" inputMode="tel" value={f.phone} onChange={(e) => set("phone", formatPhone(e.target.value))} placeholder="(555) 555-5555" autoComplete="tel" />
      </div>
      <div className="space-y-1">
        <Label htmlFor="ea-employer">Who's your employer?</Label>
        <Input id="ea-employer" value={f.employerName} onChange={(e) => set("employerName", e.target.value)} autoComplete="organization" required />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="ea-hr-name">HR / benefits contact (optional)</Label>
          <Input id="ea-hr-name" value={f.hrContactName} onChange={(e) => set("hrContactName", e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="ea-hr-email">Their email (optional)</Label>
          <Input id="ea-hr-email" type="email" value={f.hrContactEmail} onChange={(e) => set("hrContactEmail", e.target.value)} />
        </div>
      </div>
      <label className="flex items-start gap-2 text-sm text-foreground">
        <input type="checkbox" className="mt-1" checked={f.mayNameMember} onChange={(e) => set("mayNameMember", e.target.checked)} />
        <span>You may tell my employer I asked for this. (Leave unchecked and we won't share your name.)</span>
      </label>
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
      <Button type="submit" size="lg" className="w-full" disabled={state === "sending"}>
        {state === "sending" ? "Sending…" : "Request access"}
      </Button>
      <p className="text-xs text-muted-foreground">
        We use this only to contact your employer about offering Home Approach and to tell you when it's available. We never sell your
        information. See our <a href="/privacy-policy" className="underline">Privacy Policy</a>.
      </p>
    </form>
  );
}
